import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DeleteButton from '../components/DeleteButton';
import QrPrint from '../components/QrPrint';
import './ShareManage.css';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Publish a read-only copy of a place for guests, and manage its link and QR code.
function ShareManage() {
  const { id } = useParams();
  const [place, setPlace] = useState(null);
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [placeRes, shareRes] = await Promise.all([fetch(`/api/locations/${id}`), fetch(`/api/locations/${id}/share`)]);
        if (!placeRes.ok) throw new Error('This place was not found');
        const [p, s] = await Promise.all([placeRes.json(), shareRes.json()]);
        if (cancelled) return;
        setPlace(p);
        setShare(s);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const publish = async () => {
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`/api/locations/${id}/share`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Publishing failed (${response.status})`);
      setShare(data);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setError('Could not copy; press and hold the link to copy it.');
    }
  };

  if (error && !place) return <div className="share-manage"><p className="share-error">{error}</p></div>;
  if (!place || !share) return <div className="share-manage"><p>Loading...</p></div>;

  return (
    <div className="share-manage">
      <Link to={`/locations/${id}`} className="share-back">‹ Back to {place.name}</Link>
      <h1>Share “{place.name}” with guests</h1>
      <p className="share-intro">
        Guests get a read-only copy of this place and everything inside it: names, descriptions, counts and pictures. They
        can browse it or search it from a link or QR code, without signing in. They cannot change anything, and they see
        only what was published; update the copy after you make changes.
      </p>

      {error && <p className="share-error">{error}</p>}

      {!share.shared ? (
        <button type="button" className="btn btn-primary share-publish" onClick={publish} disabled={busy}>
          {busy ? 'Publishing...' : 'Publish a read-only copy'}
        </button>
      ) : (
        <>
          <div className="share-card">
            <div className="share-label">Guest link</div>
            <a className="share-url" href={share.url} target="_blank" rel="noreferrer">{share.url}</a>
            <div className="share-row">
              <button type="button" className="btn btn-secondary" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
              <a className="btn btn-secondary" href={share.url} target="_blank" rel="noreferrer">Open</a>
            </div>
            <p className="share-meta">
              Published {new Date(share.publishedAt).toLocaleString()}: {plural(share.places, 'place')} (including this
              one), {plural(share.items, 'item')} and {plural(share.pictures, 'picture')}.
            </p>
            <button type="button" className="btn btn-primary share-publish" onClick={publish} disabled={busy}>
              {busy ? 'Updating...' : 'Update the shared copy'}
            </button>
            <p className="share-hint">The link and QR code stay the same when you update, so a printed code never goes stale.</p>
          </div>

          <details className="qr-collapsible share-qr" open>
            <summary>QR code for guests</summary>
            <QrPrint qrCode={share.qrCode} name={`${place.name} guest page`} />
          </details>

          <div className="share-stop">
            <DeleteButton
              url={`/api/locations/${id}/share`}
              noun="guest link"
              warning="(the link and any printed QR code stop working immediately)"
              className="btn btn-danger"
              onDeleted={() => setShare({ shared: false })}
            >
              Stop sharing
            </DeleteButton>
          </div>
        </>
      )}
    </div>
  );
}

export default ShareManage;
